import { Navigate, Outlet, useRouteContext } from '@tanstack/react-router';
import { createRootRoute, createRoute } from '@tanstack/react-router';
import { requireGuest, requireSession } from '@/app/router/guards';
import { AppShell } from '@/layouts/app-shell';
import { ListPage, DetailPage, ProvisionPage, TenantWorkspacePage } from '@/modules/tenants';
import { ListPage as CatalogListPage, DetailPage as CatalogDetailPage, SetupPage as CatalogSetupPage } from '@/modules/catalog';
import { ListPage as MediaListPage } from '@/modules/media';
import { ListPage as InventoryListPage, DetailPage as InventoryDetailPage } from '@/modules/inventory';
import { DeniedPage } from '@/modules/auth/denied-page';
import { LoginPage } from '@/modules/auth/login-page';
import { WorkspaceUnresolvedPage } from '@/modules/auth/workspace-unresolved-page';
import { DashboardOverviewPage } from '@/modules/dashboard';
import {
  AiChatPage,
  AiConsolePage,
  AiProviderCreatePage,
  AiProviderDetailPage,
  AiTenantAvailabilityPage,
  TenantAiPage,
} from '@/modules/ai';
import { agreementRoutes } from '@/app/router/agreement-routes';

export const rootRoute = createRootRoute({
  component: function RootLayout() {
    return (
      <>
        <Outlet />
      </>
    );
  },
});

export const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: requireSession,
  context: (prevContext) => ({
    ...prevContext,
    /** Whether the assistant panel is hidden from the top bar on this route. */
    hideAssistant: false,
  }),
  component: function ShellLayout() {
    // `from` is the route's ID, not the route itself. The router keys its match store by id,
    // so a route OBJECT resolves to no match at all and the read throws.
    const { hideAssistant } = useRouteContext({ from: shellRoute.id });
    return (
      <AppShell hideAssistant={hideAssistant}>
        <Outlet />
      </AppShell>
    );
  },
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: requireGuest,
  component: function LoginRoute() {
    return <LoginPage />;
  },
});

export const deniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/denied',
  component: function DeniedRoute() {
    return <DeniedPage />;
  },
});

export const workspaceUnresolvedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workspace-unresolved',
  component: function WorkspaceUnresolvedRoute() {
    return <WorkspaceUnresolvedPage />;
  },
});

export const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: function IndexRedirect() {
    return <Navigate to="/overview" />;
  },
});


export const overviewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/overview',
  component: function OverviewRoute() {
    return <DashboardOverviewPage />;
  },
});

export const tenantsListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/tenants',
  beforeLoad: requireSession,
  component: function TenantsListRoute() {
    return <ListPage />;
  },
});

export const tenantsProvisionRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/tenants/new',
  beforeLoad: requireSession,
  component: function TenantsProvisionRoute() {
    return <ProvisionPage />;
  },
});

export const tenantsDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/tenants/$tenantId',
  beforeLoad: requireSession,
  component: function TenantsDetailRoute() {
    const { tenantId } = tenantsDetailRoute.useParams();
    return <DetailPage id={tenantId} />;
  },
});

export const tenantWorkspaceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenant/$tenantId/workspace',
  beforeLoad: requireSession,
  component: function TenantWorkspaceRoute() {
    // The page reads the tenant from the route params itself, so the route component does
    // not need to destructure it. An unused destructure here is dead code, not a hint.
    return <TenantWorkspacePage />;
  },
});

export const catalogListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/catalog/products',
  beforeLoad: requireSession,
  component: function CatalogListRoute() {
    return <CatalogListPage />;
  },
});

export const catalogDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/catalog/products/$productId',
  beforeLoad: requireSession,
  component: function CatalogDetailRoute() {
    const { productId } = catalogDetailRoute.useParams();
    return <CatalogDetailPage id={productId} />;
  },
});

export const mediaListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/catalog/media',
  beforeLoad: requireSession,
  component: function MediaListRoute() {
    return <MediaListPage />;
  },
});

export const catalogSetupRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/catalog/setup',
  beforeLoad: requireSession,
  component: function CatalogSetupRoute() {
    return <CatalogSetupPage />;
  },
});

export const inventoryListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory/stock',
  beforeLoad: requireSession,
  component: function InventoryListRoute() {
    return <InventoryListPage />;
  },
});

export const inventoryDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inventory/stock/$itemId',
  beforeLoad: requireSession,
  component: function InventoryDetailRoute() {
    const { itemId } = inventoryDetailRoute.useParams();
    return <InventoryDetailPage itemId={itemId} />;
  },
});

export const platformAiConsoleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/ai',
  beforeLoad: requireSession,
  component: function PlatformAiConsoleRoute() {
    return <AiConsolePage />;
  },
});

export const platformAiChatRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/ai/chat',
  context: (prevContext) => ({
    ...prevContext,
    hideAssistant: true,
  }),
  beforeLoad: requireSession,
  component: function PlatformAiChatRoute() {
    return <AiChatPage />;
  },
});

export const platformAiProviderCreateRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/ai/providers/new',
  beforeLoad: requireSession,
  component: function PlatformAiProviderCreateRoute() {
    return <AiProviderCreatePage />;
  },
});

export const platformAiProviderDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/ai/providers/$providerId',
  beforeLoad: requireSession,
  component: function PlatformAiProviderDetailRoute() {
    const { providerId } = platformAiProviderDetailRoute.useParams();
    return <AiProviderDetailPage providerId={providerId} />;
  },
});

export const platformAiTenantsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/platform/ai/tenants',
  beforeLoad: requireSession,
  component: function PlatformAiTenantsRoute() {
    return <AiTenantAvailabilityPage />;
  },
});

export const tenantAiRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenant/$tenantId/workspace/ai',
  beforeLoad: requireSession,
  component: function TenantAiRoute() {
    const { tenantId } = tenantAiRoute.useParams();
    return <TenantAiPage tenantId={tenantId} />;
  },
});

export const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([
    indexRoute,
    overviewRoute,
    tenantsListRoute,
    tenantsProvisionRoute,
    tenantsDetailRoute,
    tenantWorkspaceRoute,
    catalogListRoute,
    catalogDetailRoute,
    catalogSetupRoute,
    mediaListRoute,
    inventoryListRoute,
    inventoryDetailRoute,
    platformAiConsoleRoute,
    platformAiChatRoute,
    platformAiProviderCreateRoute,
    platformAiProviderDetailRoute,
    platformAiTenantsRoute,
    tenantAiRoute,
    ...agreementRoutes,
  ]),
  loginRoute,
  deniedRoute,
  workspaceUnresolvedRoute,
]);
